import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios'; // Import axios for making HTTP requests

// This is where you will define your API endpoints.
// For a production environment, you would place this in a separate file like `api.js`
const api = {
  uploadExcelFile: (file) => {
    const formData = new FormData();
    formData.append('excelFile', file);
    // This is the actual API call to your backend's file upload endpoint.
    // Ensure this URL matches your Express route for file uploads.
    return axios.post('/api/smuploader/upload-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getJobStatus: (jobId) => {
    // This is the actual API call to your backend's job status endpoint.
    // Ensure this URL matches your Express route for checking status by job ID.
    return axios.get(`/api/smuploader/status/${jobId}`);
  },
};


// Main React Component for Social Media Uploader
export default function SocialMediaUploader() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [message, setMessage] = useState('');
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Use a ref to store the interval ID so we can clear it later
  const intervalRef = useRef(null);

  // Effect to handle automatic polling for job status
  useEffect(() => {
    // If there is a job ID and the job is not completed or failed, start polling
    if (jobId && jobStatus?.status !== 'completed' && jobStatus?.status !== 'failed') {
      // Set an interval to fetch the job status every 3 seconds
      intervalRef.current = setInterval(() => {
        fetchJobStatus();
      }, 3000);
    }

    // Cleanup function to clear the interval when the component unmounts or dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [jobId, jobStatus]); // Dependencies: re-run this effect when jobId or jobStatus changes

  // Handles file selection

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
    setMessage('');
    setJobId(null);
    setJobStatus(null);
  };

  const handleImagesChange = (event) => {
    setSelectedImages(Array.from(event.target.files));
  };

  // Handles the upload button click
  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage('Please select an Excel file first.');
      return;
    }

    setMessage('Uploading and processing...');
    setIsLoading(true);
    setJobId(null);
    setJobStatus(null);

    try {
      const formData = new FormData();
      formData.append('excelFile', selectedFile);
      selectedImages.forEach((img) => {
        formData.append('images', img);
      });
      const response = await axios.post('/api/smuploader/upload-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setMessage(response.data.message);
      setJobId(response.data.jobId);
      // The job is now "processing," so we can start polling
    } catch (error) {
      console.error('Error uploading file:', error);
      setMessage(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsLoading(false);
      // Clear file input after upload attempt
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSelectedFile(null);
      setSelectedImages([]);
    }
  };

  // Fetches the status of the background job
  const fetchJobStatus = async () => {
    if (!jobId) {
      return; // No job ID to fetch status for
    }
    
    try {
      const response = await api.getJobStatus(jobId);
      setJobStatus(response.data);
      setMessage(`Job Status: ${response.data.status}`);
    } catch (error) {
      console.error('Error fetching job status:', error);
      setMessage(`Error fetching job status: ${error.message}`);
      // Stop polling on error
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  };

  // Determines the color class for the status message
  const getStatusColor = (status) => {
    if (!status) return 'text-gray-500';
    const lowerStatus = status.toLowerCase();
    switch (lowerStatus) {
      case 'completed':
      case 'success':
        return 'text-green-500';
      case 'failed':
        return 'text-red-500';
      case 'processing':
      case 'pending':
        return 'text-yellow-500';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 font-sans">
       {/* Sample Excel Download Button */}
        <a
          href="/social_media_uploader.xlsx"
          download
          className="absolute top-4 right-4 bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold py-2 px-4 rounded-lg shadow text-sm transition-colors duration-200"
        >
          Sample Excel
        </a>
      <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-2xl text-center relative">
       
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Social Media Uploader</h1>
        <p className="text-gray-600 mb-6">Upload an Excel sheet to post images to social media platforms.</p>

        <div className="flex flex-col items-center space-y-4 mb-6">
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileChange}
            ref={fileInputRef}
            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
          />
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImagesChange}
            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
          />
          <button
            onClick={handleUpload}
            disabled={!selectedFile || isLoading}
            className={`w-full px-6 py-3 rounded-lg font-semibold text-white transition-colors duration-200 ${
              !selectedFile || isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Uploading...
              </span>
            ) : 'Upload Excel & Images'}
          </button>
        </div>

        {message && (
          <p className={`text-sm mb-4 ${getStatusColor(message.split(':')[1]?.trim() || '')}`}>
            {message}
          </p>
        )}

        {jobId && (
          <div className="job-status-section border-t border-gray-200 pt-6 mt-6 text-left">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Job ID: {jobId}</h2>
            
            {jobStatus && (
              <div className="status-details">
                <p className="mb-2 text-lg font-medium text-gray-700">
                  Overall Status: <span className={`font-bold ${getStatusColor(jobStatus.status)}`}>{jobStatus.status.toUpperCase()}</span>
                </p>
                {jobStatus.processedPosts && jobStatus.processedPosts.length > 0 && (
                  <div>
                    <h4 className="text-xl font-semibold text-gray-800 mt-4 mb-2">Individual Post Results:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {jobStatus.processedPosts.map((post, index) => (
                        <div key={index} className={`post-card p-4 rounded-lg shadow-sm border ${
                          post.status === 'success' ? 'bg-green-50 border-green-200' :
                          post.status === 'failed' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                        }`}>
                          <p className="font-bold text-gray-800">Image: <span className="font-normal">{post.imageFileName}</span></p>
                          <p className="text-gray-600">Platform: {post.platform}</p>
                          <p className="text-gray-600">Account: {post.accountId}</p>
                          <p className="font-bold text-gray-800">Status: <span className={`font-normal ${getStatusColor(post.status)}`}>{post.status.toUpperCase()}</span></p>
                          {post.errorMessage && <p className="text-sm text-red-500 mt-1">Error: {post.errorMessage}</p>}
                          {post.postUrl && <p className="text-sm mt-1">URL: <a href={post.postUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{post.postUrl}</a></p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}